import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, message, Tag, Tooltip } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import RuleFormDrawer from './components/RuleFormDrawer';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
  GET_ADDRESS_KEYWORD_RULE_LIST,
  UPDATE_ADDRESS_KEYWORD_RULE_STATUS,
  DELETE_ADDRESS_KEYWORD_RULE,
  GetAddressKeywordRuleListResponse,
  AddressKeywordRuleType,
  STATUS_ENABLED,
  STATUS_DISABLED,
} from './graphql/addressKeywordRuleGraphql';

const AddressKeywordRulePage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerKey, setDrawerKey] = useState(0);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressKeywordRuleListResponse>(
    GET_ADDRESS_KEYWORD_RULE_LIST,
    {
      variables: {
        keywordFrom: appliedFilters.keywordFrom || undefined,
        keywordTo: appliedFilters.keywordTo || undefined,
        status: appliedFilters.status ? Number(appliedFilters.status) : undefined,
        state: appliedFilters.state || undefined,
        city: appliedFilters.city || undefined,
        zipCode: appliedFilters.zipCode || undefined,
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [updateStatus] = useMutation(UPDATE_ADDRESS_KEYWORD_RULE_STATUS, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const [deleteRule] = useMutation(DELETE_ADDRESS_KEYWORD_RULE, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openCreate = () => {
    setDrawerKey((k) => k + 1);
    setDrawerVisible(true);
  };

  const showDeleteConfirm = (record: AddressKeywordRuleType) => {
    Modal.confirm({
      title: t('mapTool.addressKeywordRule.deleteTitle') as string,
      content: t('mapTool.addressKeywordRule.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteRule({ variables: { id: Number(record.id) } }),
    });
  };

  const toggleStatus = (record: AddressKeywordRuleType) => {
    const nextStatus = record.status === STATUS_ENABLED ? STATUS_DISABLED : STATUS_ENABLED;
    updateStatus({ variables: { id: Number(record.id), status: nextStatus } });
  };

  const renderNullable = (v: string | null | undefined) => v ?? '—';

  const columns: TableColumnsType<AddressKeywordRuleType> = [
    {
      title: t('mapTool.addressKeywordRule.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressKeywordRule.table.keywordFrom'),
      dataIndex: 'keywordFrom',
      key: 'keywordFrom',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressKeywordRule.table.keywordTo'),
      dataIndex: 'keywordTo',
      key: 'keywordTo',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressKeywordRule.table.state'),
      dataIndex: 'state',
      key: 'state',
      width: 100,
      align: 'center',
      render: renderNullable,
    },
    {
      title: t('mapTool.addressKeywordRule.table.city'),
      dataIndex: 'city',
      key: 'city',
      width: 140,
      ellipsis: true,
      render: renderNullable,
    },
    {
      title: t('mapTool.addressKeywordRule.table.zipCode'),
      dataIndex: 'zipCode',
      key: 'zipCode',
      width: 180,
      ellipsis: true,
      render: (v: string | null | undefined) => (v ? <Tooltip title={v}>{v}</Tooltip> : '—'),
    },
    {
      title: t('mapTool.addressKeywordRule.table.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      align: 'center',
    },
    {
      title: t('mapTool.addressKeywordRule.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (v: number) =>
        v === STATUS_ENABLED ? (
          <Tag color="green">{t('mapTool.addressKeywordRule.status.enabled')}</Tag>
        ) : (
          <Tag color="default">{t('mapTool.addressKeywordRule.status.disabled')}</Tag>
        ),
    },
    {
      title: t('mapTool.addressKeywordRule.table.testAddress'),
      dataIndex: 'testAddress',
      key: 'testAddress',
      width: 260,
      ellipsis: true,
      render: renderNullable,
    },
    {
      title: t('mapTool.addressKeywordRule.table.testResult'),
      dataIndex: 'testResult',
      key: 'testResult',
      width: 260,
      ellipsis: true,
      render: renderNullable,
    },
    {
      title: t('mapTool.addressKeywordRule.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      align: 'center',
      render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 160,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <HasResource resourceCodes={[ResourceCode.addressKeywordRule.UPDATE]}>
            <Button type="link" size="small" onClick={() => toggleStatus(record)}>
              {record.status === STATUS_ENABLED
                ? t('mapTool.addressKeywordRule.actions.disable')
                : t('mapTool.addressKeywordRule.actions.enable')}
            </Button>
          </HasResource>
          <HasResource resourceCodes={[ResourceCode.addressKeywordRule.DELETE]}>
            <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
              {t('components.button.delete')}
            </Button>
          </HasResource>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressKeywordRuleList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressKeywordRule.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <HasResource resourceCodes={[ResourceCode.addressKeywordRule.CREATE]}>
            <Button type="primary" onClick={openCreate} className="w-auto min-w-24">
              <span className="font-bold">{t('components.button.new')}</span>
            </Button>
          </HasResource>
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => t('mapTool.addressKeywordRule.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <RuleFormDrawer
        key={drawerKey}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default AddressKeywordRulePage;
